import { useState } from "react";
import { Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { interpolate, useLocale } from "@/i18n/context";

interface UserRatingFormProps {
  onRatingSubmitted?: () => void;
}

export const UserRatingForm = ({ onRatingSubmitted }: UserRatingFormProps) => {
  const { t } = useLocale();
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [selectedStar, setSelectedStar] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStarClick = async (star: number) => {
    if (isSubmitting || selectedStar) return;
    
    setIsSubmitting(true);
    setSelectedStar(star);

    const { data: { user } } = await supabase.auth.getUser();

    const { error } = await supabase
      .from('user_ratings')
      .insert({
        rating: star,
        user_id: user?.id || null
      });

    setIsSubmitting(false);

    if (error) {
      setSelectedStar(null);
      toast({
        title: t.common.error,
        description: t.userRating.saveError,
        variant: "destructive"
      });
    } else {
      toast({
        title: t.userRating.thanksToast,
        description: t.userRating.thanksToastDesc
      });
      onRatingSubmitted?.();
    }
  };

  return (
    <div className="text-center">
      <h2 className="text-2xl md:text-3xl font-bold mb-2">
        {t.userRating.title}
      </h2>
      <p className="text-muted-foreground mb-8">
        {t.userRating.subtitle}
      </p>
      
      <div className="flex items-center justify-center gap-3">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onMouseEnter={() => !selectedStar && setHoveredStar(star)}
            onMouseLeave={() => setHoveredStar(null)}
            onClick={() => handleStarClick(star)}
            disabled={isSubmitting || !!selectedStar}
            className={`p-2 transition-all duration-200 ${
              selectedStar 
                ? "cursor-default" 
                : "hover:scale-125 cursor-pointer"
            }`}
            aria-label={interpolate(t.userRating.rateAria, { star })}
          >
            <Star
              className={`w-12 h-12 md:w-16 md:h-16 transition-all duration-200 ${
                (hoveredStar !== null && star <= hoveredStar) ||
                (hoveredStar === null && selectedStar !== null && star <= selectedStar)
                  ? "fill-accent text-accent drop-shadow-[0_0_12px_rgba(238,189,43,0.6)]"
                  : selectedStar
                  ? "fill-transparent text-muted-foreground/30"
                  : "fill-transparent text-muted-foreground/50 hover:text-accent"
              }`}
            />
          </button>
        ))}
      </div>
      
      {selectedStar && (
        <div className="mt-8 animate-in fade-in slide-in-from-bottom-4">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-6 py-3 rounded-full font-medium">
            <Star className="w-5 h-5 fill-primary" />
            {t.userRating.thanks}
          </div>
        </div>
      )}
    </div>
  );
};
